<template>
  <div class="plan-selection">
    <div class="flex justify-center mb-8">
      <div class="bg-white p-1 rounded-full border border-gray-200 inline-flex shadow-sm">
        <button
          class="px-6 py-2 rounded-full text-sm font-bold transition-all duration-200 focus:outline-none"
          :class="
            billingCycle === 'monthly'
              ? 'bg-gray-900 text-white shadow-md'
              : 'text-gray-500 hover:text-gray-900'
          "
          @click="billingCycle = 'monthly'"
        >
          Monthly
        </button>
        <button
          class="px-6 py-2 rounded-full text-sm font-bold transition-all duration-200 focus:outline-none flex items-center"
          :class="
            billingCycle === 'yearly'
              ? 'bg-gray-900 text-white shadow-md'
              : 'text-gray-500 hover:text-gray-900'
          "
          @click="billingCycle = 'yearly'"
        >
          Yearly
          <span
            v-if="billingCycle !== 'yearly'"
            class="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full"
          >
            -20%
          </span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="flex justify-center items-center h-40">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>

    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <v-card
        v-for="plan in currentPlans"
        :key="plan.name"
        class="flex flex-col h-full rounded-xl overflow-hidden transition-all duration-300 hover:shadow-xl cursor-pointer relative border"
        :class="[
          selectedPlan === plan.name
            ? `ring-2 ring-offset-2 ring-${plan.colorName}-500 shadow-lg transform scale-[1.02]`
            : 'border-gray-100'
        ]"
        @click="selectPlan(plan)"
      >
        <!-- Popular Badge -->
        <div
          v-if="plan.popular"
          class="absolute top-0 right-0 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg z-10"
        >
          POPULAR
        </div>

        <!-- Selection Indicator -->
        <div v-if="selectedPlan === plan.name" class="absolute top-3 left-3 text-blue-500">
          <v-icon color="blue">mdi-check-circle</v-icon>
        </div>

        <!-- Plan Header -->
        <div class="p-6 text-center bg-white border-b border-gray-50">
          <h3
            class="text-lg font-bold mb-2 uppercase tracking-wide"
            :class="`text-${plan.colorName}-600`"
          >
            {{ plan.name }}
          </h3>
          <div class="flex justify-center items-baseline mb-1">
            <span class="text-4xl font-extrabold text-gray-900">₹{{ plan.price }}</span>
            <span class="text-gray-500 ml-1 font-medium text-sm"
              >/{{ billingCycle === 'monthly' ? 'mo' : 'yr' }}</span
            >
          </div>
          <p class="text-xs text-gray-400 mt-1 h-4">
            {{ billingCycle === 'yearly' ? 'Billed annually' : '' }}
          </p>
        </div>

        <!-- Features -->
        <div class="p-6 flex-grow bg-gray-50/50">
          <ul class="space-y-4">
            <li v-for="feature in plan.features" :key="feature" class="flex items-start">
              <v-icon small color="green" class="mt-0.5 mr-3 flex-shrink-0"
                >mdi-check-circle</v-icon
              >
              <span class="text-sm text-gray-600 font-medium leading-relaxed text-left">{{
                feature
              }}</span>
            </li>
          </ul>
        </div>

        <!-- Action -->
        <div class="p-6 bg-white mt-auto" v-if="showActions">
          <v-btn
            block
            large
            depressed
            :color="plan.name === 'Base' ? 'grey lighten-3' : 'primary'"
            :class="plan.name === 'Base' ? 'grey--text text--darken-2' : 'white--text'"
            class="rounded-lg font-bold tracking-wide"
            @click.stop="confirmPlan(plan)"
            :loading="processingPlan === plan.name"
            :disabled="
              currentPlanRefId === plan._id &&
              billingCycle.toLowerCase() === currentBillingCycle.toLowerCase()
            "
          >
            {{ getButtonText(plan) }}
          </v-btn>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PlanSelection',
  props: {
    currentPlanRefId: {
      type: String,
      default: ''
    },
    processingPlan: {
      type: String,
      default: null
    },
    showActions: {
      type: Boolean,
      default: true
    },
    currentBillingCycle: {
      type: String,
      default: 'monthly'
    }
  },
  data() {
    return {
      loading: false,
      billingCycle: 'monthly',
      selectedPlan: null,
      rawPlans: []
    }
  },
  computed: {
    currentPlans() {
      return this.rawPlans.map((plan) => ({
        ...plan,
        price: this.billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice,
        priceId: this.billingCycle === 'monthly' ? plan.monthlyPriceId : plan.yearlyPriceId
      }))
    }
  },
  async mounted() {
    await this.fetchPlans()
    if (this.currentPlanRefId) {
      this.selectedPlan = this.currentPlanRefId || 'null'
    } else {
      // Default to Base or first
      this.selectedPlan = 'Free'
    }
    // Emit initial billing cycle
    this.$emit('billing-cycle-change', this.billingCycle)

    // Emit initial plan details
    const initialPlan = this.currentPlans.find((p) => p.name === this.selectedPlan)
    if (initialPlan) {
      this.$emit('plan-click', initialPlan)
    }
  },
  watch: {
    billingCycle(val) {
      this.$emit('billing-cycle-change', val)
    }
  },
  methods: {
    async fetchPlans() {
      this.loading = true
      try {
        const response = await this.$axios.get('/cualitywork/plans/seed')
        if (response.data && response.data.plans) {
          this.rawPlans = response.data.plans.map((plan) => {
            return {
              ...plan,
              monthlyPrice: plan.amount,
              yearlyPrice: Math.round(plan.amount * 12 * 0.8), // 20% discount
              colorName: this.getColorForPlan(plan.name),
              popular: plan.name === 'Silver'
            }
          })
        }
      } catch (error) {
        console.error('Error fetching plans:', error)
      } finally {
        this.loading = false
      }
    },
    getColorForPlan(name) {
      const colors = {
        Free: 'gray',
        Silver: 'blue',
        Gold: 'amber',
        Diamond: 'cyan'
      }
      return colors[name] || 'gray'
    },
    selectPlan(plan) {
      this.selectedPlan = plan.name
      this.$emit('plan-click', plan)
    },
    confirmPlan(plan) {
      this.selectedPlan = plan.name
      this.$emit('select-plan', { ...plan, billingCycle: this.billingCycle })
    },
    getButtonText(plan) {
      if (
        this.currentPlanRefId === plan._id &&
        this.billingCycle.toLowerCase() === this.currentBillingCycle.toLowerCase()
      ) {
        return 'Current Plan'
      }
      return 'Choose ' + plan.name
    }
  }
}
</script>

<style scoped>
/* Tailwind utility classes are mostly used, adding some scoped details if needed */
</style>
